import { Alert, Box, CircularProgress, useTheme } from "@mui/material";
import * as React from "react";

import {
  type BaseTableProps,
  DatasetsTable,
  TopicsTable,
  TopicMessagePathsTable,
  EventResultsTable,
} from "@/shared/components/SearchResultViews";
import { getQueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useInfiniteQueryResults,
  useQueryCount,
  useQueryRecord,
} from "@/shared/domain/query/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { Dataset } from "@/types";

import { FileResults } from "./FileResults";
import { OnResultInteraction, SearchResultInteraction } from "./interaction";

const noop = () => {};

export interface SearchResultsProps {
  queryId?: string;
  lastError: Error | null;
  onError: (error: Error | null) => void;
  onLoaded?: () => void;
  onResultInteraction?: OnResultInteraction;
  selectedDatasets?: Set<string>;
  setSelectedDatasets?: (arg: Set<string>) => void;
  extraHeight?: number;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  queryId,
  lastError,
  onError = noop,
  onLoaded = noop,
  onResultInteraction,
  selectedDatasets,
  setSelectedDatasets,
  extraHeight = 0,
}) => {
  const theme = useTheme();
  const queryRecordQuery = useQueryRecord(queryId);
  const queryRecord = queryRecordQuery.data;

  // Use a fixed backend page size of 50 for all queries
  // UI pagination is independent and just slices the cached results
  const resultsQuery = useInfiniteQueryResults(queryRecord?.query_id, {
    pageSize: 50,
  });
  const countQuery = useQueryCount(queryRecord?.query_id);

  const results = React.useMemo(() => resultsQuery.data ?? [], [resultsQuery]);
  const count = countQuery.data;

  const isLoading = queryRecordQuery.isLoading || resultsQuery.isLoading;
  const queryError = queryRecordQuery.error || resultsQuery.error;

  const maxTableHeight = `calc(100vh - ${
    theme.navTopBarHeight
  } - ${parseInt(theme.spacing(2.5))}px - 56px - ${parseInt(theme.spacing(2))}px 
  - 52px - ${parseInt(theme.spacing(2))}px - ${extraHeight}px)`;

  const fetchMoreData = React.useCallback(() => {
    if (!resultsQuery.isFetching) {
      resultsQuery
        .fetchNextPage()
        .catch((err) => ErrorMonitoringService.captureError(err));
    }
  }, [resultsQuery]);

  const makeBaseProps = React.useCallback(
    function makeBaseProps<T>(): BaseTableProps<T> {
      return {
        data: (results as T[]) || [],
        dataCount: count,
        loading: isLoading,
        fetchMoreData,
        containerStyle: {
          transition: "max-width 0.3s ease-in-out",
          maxHeight: maxTableHeight,
        },
      };
    },
    [results, count, isLoading, fetchMoreData, maxTableHeight],
  );

  React.useEffect(() => {
    onError(queryError);
  }, [onError, queryError]);

  React.useEffect(() => {
    if (!isLoading) {
      onLoaded();
    }
  }, [onLoaded, isLoading]);

  if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Datasets
  ) {
    const contentMode = getQueryContentMode(queryRecord);

    const onDatasetSingleClick = (dataset: Dataset) => {
      if (onResultInteraction) {
        onResultInteraction(
          dataset,
          QueryTarget.Datasets,
          SearchResultInteraction.SingleClick,
        );
      }
    };

    return (
      <DatasetsTable
        {...makeBaseProps()}
        selectedDatasets={selectedDatasets}
        setSelectedDatasets={setSelectedDatasets}
        onDatasetSingleClick={onDatasetSingleClick}
        contentMode={contentMode}
      />
    );
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Files
  ) {
    return <FileResults {...makeBaseProps()} extraHeight={extraHeight} />;
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Topics
  ) {
    {
      return (
        <TopicsTable
          {...makeBaseProps()}
          selectedRows={undefined}
          setSelectedRows={noop}
          onRowSingleClick={noop}
        />
      );
    }
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.TopicMessagePaths
  ) {
    return (
      <TopicMessagePathsTable
        {...makeBaseProps()}
        selectedRows={undefined}
        setSelectedRows={noop}
        onRowSingleClick={noop}
      />
    );
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Events
  ) {
    return <EventResultsTable {...makeBaseProps()} />;
  } else if (!isLoading && queryRecord && queryRecord.target) {
    return (
      <Box mt={theme.spacing(2)}>
        <Alert severity="error">
          Search results cannot be displayed. Please contact support@roboto.ai
          if this problem persists.
        </Alert>
      </Box>
    );
  } else if (!queryError && !lastError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: theme.spacing(3),
        }}
      >
        <CircularProgress size="2rem" />
      </Box>
    );
  }
};
